import { useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  ProcessingBasisNote,
  Select,
  StatCard,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints } from '../state/api.js';
import { jadwalManusiawi } from '../lib/jadwal.js';

/**
 * Empat jadwal yang ditawarkan layar. Nilainya ekspresi cron karena itulah yang
 * disimpan `saved_report.schedule_cron` dan dibaca penjadwal; labelnya dibangun
 * `jadwalManusiawi()` sehingga kedua bahasa lahir dari katalog yang sama dengan
 * kolom "Schedule" di tabel — satu sumber kalimat, bukan dua yang dapat berbeda.
 */
const PILIHAN_JADWAL = ['', '0 7 1 * *', '0 7 * * 1', '0 7 * * *'] as const;
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate, formatNumber } from '../lib/format.js';

/**
 * LAPORAN & ANALITIK — Figma `114:748` · M11 · rute `/reports` · izin MGR KON, LDR terbatas.
 *
 * ATURAN HITUNG PETA §5.6 TERLIHAT DI LAYAR, BUKAN HANYA DI SERVER
 * ---------------------------------------------------------------
 * *"hanya butir terverifikasi masuk angka dampak"*. Kartu dampak karena itu punya DUA
 * angka yang tidak dijumlahkan: yang terverifikasi, dan berapa klaim yang DIKELUARKAN
 * darinya. Sebuah angka dampak tanpa keterangan kedua tidak dapat dipertanggungjawabkan —
 * pembacanya tidak punya cara tahu apakah ia kecil karena tidak ada penghematan atau
 * karena tidak ada yang mengesahkannya.
 *
 * LIMA KONDISI PETA §5.6, DAN DUA DI ANTARANYA MUDAH TERTUKAR
 * ---------------------------------------------------------
 * baku · **periode tanpa data** · sedang menghitung · ekspor sedang disiapkan · ekspor
 * gagal.
 *
 * "Periode tanpa data" dan "belum ada yang menghitung" adalah DUA hal, dan server
 * membedakannya dengan `aggregates_ready`. Sebuah layar yang menampilkan "tidak ada temuan
 * pada periode ini" ketika sesungguhnya pekerja agregasi belum pernah berjalan mengatakan
 * sesuatu yang salah tentang pabriknya.
 *
 * EKSPOR: APA YANG BEKERJA HARI INI, DAN APA YANG BELUM
 * ---------------------------------------------------
 * Unduhan CSV dari baris yang BENAR-BENAR ada di layar bekerja hari ini — ia disusun di
 * peramban, tanpa dependensi, seperti ekspor pada Daftar Temuan. Laporan TERJADWAL
 * (XLSX/PDF ke penerima) menuntut pekerja penghasil berkas yang belum digelar, dan server
 * mengatakannya lewat `export_worker_configured: false`. Layar ini meneruskan kalimat itu
 * apa adanya alih-alih menawarkan tombol yang menghasilkan berkas kosong.
 */
export function Reports() {
  const { t, locale, translator } = useSession();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const data = useResource(
    async () => {
      const [metrics, catalog, saved] = await Promise.all([
        endpoints.metrics(from, to),
        endpoints.reportCatalog(),
        endpoints.savedReports(),
      ]);
      return { metrics, catalog, saved };
    },
    [from, to],
  );

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', report_kind: '', format: 'XLSX', cron: '0 7 1 * *', recipient: '' });
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'warning'; text: string } | null>(null);

  async function simpanLaporan() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.createSavedReport({
        code: form.code.trim(),
        name: form.name.trim(),
        report_kind: form.report_kind,
        format: form.format,
        schedule_cron: form.cron.trim() === '' ? null : form.cron.trim(),
        recipients: form.recipient.trim() === '' ? [] : [{ email: form.recipient.trim() }],
      });
      setToast({ kind: 'success', text: t('reports.saved.created', { code: form.code.trim() }) });
      setCreating(false);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function jalankanSekarang(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      const run = await endpoints.runSavedReport(id);
      /**
       * `202 Accepted` dan `state: QUEUED` — dan layar mengatakan ITU, bukan "laporan
       * terkirim". Sebuah kalimat yang menjanjikan berkas yang belum ada adalah kelas
       * cacat D-02, pada permukaan yang berbeda.
       */
      setToast({
        kind: data.data?.catalog.export_worker_configured ? 'success' : 'warning',
        text: t(
          data.data?.catalog.export_worker_configured ? 'reports.run.queued' : 'reports.run.noWorker',
          { state: t(`reports.runState.${run.state}`) },
        ),
      });
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function hapus(id: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.deleteSavedReport(id);
      setToast({ kind: 'success', text: t('reports.saved.deleted') });
      setDeleting(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ekspor CSV dari baris yang ADA DI LAYAR. Tanpa dependensi: `Blob` +
   * `URL.createObjectURL` + satu `<a download>`. Nilainya di-quote dan tanda kutipnya
   * digandakan, karena judul temuan yang memuat koma adalah hal biasa di lantai pabrik.
   */
  function unduhCsv(rows: Array<Record<string, unknown>>, namaBerkas: string) {
    if (rows.length === 0) return;
    const kolom = Object.keys(rows[0] as Record<string, unknown>);
    const kutip = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      kolom.map(kutip).join(','),
      ...rows.map((r) => kolom.map((k) => kutip(r[k])).join(',')),
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = namaBerkas;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Screen
      title={t('reports.title')}
      subtitle={t('reports.subtitle')}
      resource={data}
      moduleCode="M11"
      aside={
        <div className="filters">
          <Input
            label={t('reports.period.from')}
            inputType="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <Input
            label={t('reports.period.to')}
            inputType="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          <div className="stat-grid">
            <StatCard
              label={t('reports.stat.verifiedImpact')}
              value={t('reports.money', {
                amount: formatNumber(loaded.metrics.verified_impact.amount, locale),
                currency: loaded.metrics.verified_impact.currency,
              })}
              caption={t('reports.stat.verifiedImpact.caption', {
                claims: loaded.metrics.verified_impact.claim_count,
              })}
            />
            {/*
              KLAIM YANG DIKELUARKAN — angka kedua yang membuat angka pertama dapat
              dipertanggungjawabkan (peta §5.6, dan alur 5 §6). Ia TIDAK dijumlahkan ke
              dampak terverifikasi, dan nadanya peringatan hanya bila ada yang menunggu.
            */}
            <StatCard
              label={t('reports.stat.excluded')}
              value={String(loaded.metrics.verified_impact.excluded_unverified_count)}
              tone={loaded.metrics.verified_impact.excluded_unverified_count > 0 ? 'warning' : 'neutral'}
              caption={t('reports.stat.excluded.caption', {
                amount: formatNumber(loaded.metrics.verified_impact.excluded_unverified_amount, locale),
                currency: loaded.metrics.verified_impact.currency,
              })}
            />
            <StatCard
              label={t('reports.stat.opened')}
              value={formatNumber(
                loaded.metrics.daily.reduce((n, d) => n + d.opened, 0),
                locale,
              )}
              caption={t('reports.stat.period', {
                from: formatDate(loaded.metrics.period.from, locale),
                to: formatDate(loaded.metrics.period.to, locale),
              })}
            />
            <StatCard
              label={t('reports.stat.closed')}
              value={formatNumber(
                loaded.metrics.daily.reduce((n, d) => n + d.closed, 0),
                locale,
              )}
            />
          </div>

          <section className="card">
            <div className="page__head">
              <h2 className="card__title">{t('reports.byCategory')}</h2>
              <Button
                variant="secondary"
                onClick={() =>
                  unduhCsv(
                    loaded.metrics.by_category as unknown as Array<Record<string, unknown>>,
                    `assist-kategori-${loaded.metrics.period.from}-${loaded.metrics.period.to}.csv`,
                  )
                }
              >
                {t('reports.exportCsv')}
              </Button>
            </div>

            {/*
              DUA KONDISI YANG BERBEDA, dan server yang membedakannya:
              `aggregates_ready: false` berarti belum ada yang menghitung; `daily` kosong
              dengan `aggregates_ready: true` berarti periodenya memang tanpa data.
            */}
            {!loaded.metrics.aggregates_ready ? (
              <AlertBanner
                kind="information"
                signalWord={t('signal.information')}
                title={t('reports.aggregatesPending')}
              />
            ) : loaded.metrics.by_category.length === 0 ? (
              <EmptyState
                title={t('reports.noData.title')}
                body={t('reports.noData.body')}
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('findings.column.category')}</th>
                    <th scope="col">{t('reports.column.opened')}</th>
                    <th scope="col">{t('reports.column.closed')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.metrics.by_category.map((row) => (
                    <tr key={row.category_code ?? 'none'}>
                      <td>{row.category_code ? t(`category.${row.category_code}`) : '—'}</td>
                      <td>{formatNumber(row.opened, locale)}</td>
                      <td>{formatNumber(row.closed, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <div className="page__head">
              <h2 className="card__title">{t('reports.saved')}</h2>
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t('reports.saved.add')}
              </Button>
            </div>

            {!loaded.catalog.export_worker_configured ? (
              <AlertBanner
                kind="warning"
                signalWord={t('signal.warning')}
                title={t('reports.exportWorkerMissing')}
              />
            ) : null}

            {loaded.saved.items.length === 0 ? (
              <p className="card__muted">{t('reports.saved.empty')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('reports.column.name')}</th>
                    <th scope="col">{t('reports.column.kind')}</th>
                    <th scope="col">{t('reports.column.schedule')}</th>
                    <th scope="col">{t('reports.column.recipients')}</th>
                    <th scope="col">{t('reports.column.lastRun')}</th>
                    <th scope="col">{t('locations.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.saved.items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {row.name}
                        <br />
                        <span className="card__muted">{row.code}</span>
                      </td>
                      <td>
                        {t(`reports.kind.${row.report_kind}`)}
                        <br />
                        <Badge tone="neutral">{row.format}</Badge>
                      </td>
                      {/*
                        Kolom ini dulu merender `schedule_cron` apa adanya, sehingga
                        seorang manajer pabrik membaca `0 7 1 * *`. Lihat
                        `apps/console/src/lib/jadwal.ts` untuk alasan terjemahannya
                        hidup di konsol dan bukan di server.
                      */}
                      <td>{jadwalManusiawi(row.schedule_cron, t)}</td>
                      <td>{row.recipients.length}</td>
                      <td>
                        {row.last_run_at ? formatDate(row.last_run_at, locale) : t('reports.neverRun')}
                      </td>
                      <td className="page__actions">
                        <Button variant="secondary" busy={busy} onClick={() => void jalankanSekarang(row.id)}>
                          {t('reports.runNow')}
                        </Button>
                        <Button variant="text" onClick={() => setDeleting(row.id)}>
                          {t('reports.saved.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {loaded.saved.processing_basis ? (
              <ProcessingBasisNote
                label={t('privacy.basisNote.label')}
                sentence={t('privacy.basisNote.sentence', {
                  basis: t(`privacy.basis.${loaded.saved.processing_basis.legal_basis}`),
                })}
                article={loaded.saved.processing_basis.article}
                purposes={t('privacy.basisNote.purposes', {
                  purposes: loaded.saved.processing_basis.purposes
                    .map((p) => t(`privacy.purpose.${p}`))
                    .join(', '),
                })}
                moreLabel={t('privacy.basisNote.more')}
              />
            ) : null}
          </section>

          <Dialog
            open={creating}
            kind="confirm"
            title={t('reports.saved.add')}
            description={t('reports.saved.add.describe')}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={
              form.code.trim().length < 3 || form.name.trim().length < 3 || form.report_kind === ''
            }
            onConfirm={() => void simpanLaporan()}
            onCancel={() => setCreating(false)}
          >
            <Input
              label={t('reports.field.code')}
              value={form.code}
              maxLength={40}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
            <Input
              label={t('reports.column.name')}
              value={form.name}
              maxLength={160}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <Select
              label={t('reports.column.kind')}
              value={form.report_kind}
              onChange={(e) => setForm({ ...form, report_kind: e.target.value })}
              options={[
                { value: '', label: t('report.field.choose') },
                ...loaded.catalog.items.map((c) => ({
                  value: c.code,
                  label: t(`reports.kind.${c.code}`),
                })),
              ]}
            />
            <Select
              label={t('reports.field.format')}
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}
              options={loaded.catalog.formats.map((f) => ({ value: f, label: f }))}
            />
            {/*
              PILIHAN, BUKAN KOTAK ISIAN CRON.
              Medan ini dulu meminta manajer pabrik MENGETIK `0 7 1 * *`. Personanya
              (peta §2) adalah orang yang memimpin produksi, bukan orang yang menulis
              penjadwal — dan sebuah kotak yang menuntut sintaks yang tidak ia kenal
              menghasilkan satu dari dua hal: laporan yang tidak pernah dibuat, atau
              laporan yang terkirim pada waktu yang tidak ia maksud. Empat pilihan
              menutup kebutuhan sebenarnya, dan nilainya tetap cron di basis data.
            */}
            <Select
              label={t('reports.field.schedule')}
              value={form.cron}
              onChange={(e) => setForm({ ...form, cron: e.target.value })}
              options={PILIHAN_JADWAL.map((c) => ({ value: c, label: jadwalManusiawi(c, t) }))}
              hint={t('reports.field.schedule.hint')}
            />
            <Input
              label={t('reports.field.recipient')}
              inputType="email"
              value={form.recipient}
              onChange={(e) => setForm({ ...form, recipient: e.target.value })}
            />
          </Dialog>

          <Dialog
            open={deleting !== null}
            kind="destructive"
            title={t('reports.saved.delete')}
            description={t('reports.saved.delete.describe')}
            confirmLabel={t('reports.saved.delete')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => {
              if (deleting) void hapus(deleting);
            }}
            onCancel={() => setDeleting(null)}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind={toast.kind}
                signalWord={toast.kind === 'success' ? t('signal.success') : t('signal.warning')}
                title={toast.text}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}

/**
 * Byte order mark UTF-8, ditulis sebagai KODE dan bukan sebagai karakter.
 *
 * Excel di Windows membaca CSV tanpa BOM sebagai Latin-1, dan "Tumpukan palét" menjadi
 * "Tumpukan palÃ©t" pada laporan yang dibuka manajer. Karakternya sendiri tidak boleh
 * berdiri di kode: ia spasi tak terlihat, dan lint `no-irregular-whitespace` menolaknya
 * justru supaya sebuah spasi tak terlihat tidak dapat masuk ke kode tanpa disengaja.
 */
const BOM = '\uFEFF';

function monthStart(): string {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}
